import * as React from "react";
import { getPublicRuntimeConfig } from "../../config";
import { getBranchLinkForProcedure, isBranchLoaded } from "../../core/branch";
import { useBooleanState } from "../../core/hooks/useBooleanState";
import useIntersection from "../../core/hooks/useIntersection";
import { useTabletLargeMediaQuery } from "../../core/hooks/useMediaQuery";
import { getMaintainXUrl } from "../../core/maintainxUrl";
import { getUrlOpener } from "../../core/utils";
import { getUtmParameters } from "../../core/utmParameters";
import { trackGPLEvent } from "../../providers/GoogleTagManager/GoogleTagManagerProvider";
import { IProcedureDetails } from "../../typings/filterEntities";
import Button from "../Button/Button";
import ExternalLink from "../ExternalLink/ExternalLink";
import { DownloadIcon } from "../Icons/Icons";
import ModalDownloadPdf, { DownloadPDFCallback } from "../ModalDownloadPdf/ModalDownloadPdf";
import ModalSendToPhone from "../ModalSendToPhone/ModalSendToPhone";
import styles from "./ProcedureDownload.module.css";

interface IProps {
  procedure: IProcedureDetails;
  onDownload: DownloadPDFCallback;
}

const intersectionObserverOptions = {
  root: null,
  rootMargin: "-80px", // height of mobile header
  threshold: 0,
};

export default function ProcedureDownload({ procedure, onDownload }: IProps) {
  const [isShowingDownloadModal, showDownloadModal, hideDownloadModal] = useBooleanState();
  const [isShowingSendModal, showSendModal, hideSendModal] = useBooleanState();
  const isTabletLargeViewport = useTabletLargeMediaQuery();

  const [isShowingFloatingButton, showFloatingButton, hideFloatingButton] = useBooleanState();
  const staticButtonRef = React.useRef<HTMLDivElement>(null);
  const staticButtonIntersection = useIntersection(staticButtonRef, intersectionObserverOptions);

  const [branchLoaded, setBranchLoaded] = React.useState(false);

  React.useEffect(() => {
    if (staticButtonIntersection) {
      if (staticButtonIntersection.isIntersecting || isTabletLargeViewport) {
        hideFloatingButton();
      } else if (
        // attempt at only showing the floating button when scrolling down the page
        staticButtonIntersection.boundingClientRect.y <
          staticButtonIntersection.boundingClientRect.height &&
        !isTabletLargeViewport
      ) {
        showFloatingButton();
      }
    }
  }, [hideFloatingButton, showFloatingButton, isTabletLargeViewport, staticButtonIntersection]);

  React.useEffect(() => {
    if (isBranchLoaded()) {
      setBranchLoaded(true);
    } else {
      // GTM adds an init callback to the branch initialization that emits this event on window once initialized

      const handler = () => {
        setBranchLoaded(true);
      };
      window.addEventListener("branch-initialized", handler);

      return () => {
        window.removeEventListener("branch-initialized", handler);
      };
    }
  }, []);

  const desktopUrl = `${getMaintainXUrl()}${getUtmParameters({ procedureUid: procedure.uid })}`;

  const canUseBranch = branchLoaded;

  const handleTagOpenInDesktop = React.useCallback(() => {
    trackGPLEvent("OpenInMaintainX", {
      action: "Share",
      category: "Procedure",
      label: procedure.uid,
    });
  }, [procedure.uid]);

  const handleOpenDeepLink = React.useCallback(async () => {
    const urlOpener = getUrlOpener("Loading…");
    trackGPLEvent("OpenInMaintainX", {
      action: "Download",
      category: "Procedure",
      label: procedure.uid,
    });
    if (canUseBranch) {
      const link = await getBranchLinkForProcedure(procedure.uid, "OpenInMaintainX");
      urlOpener.openUrl(link);
    } else {
      urlOpener.openUrl(
        `${getPublicRuntimeConfig().branchFallbackUrl}?gpl_procedure_uid=${procedure.uid}`,
      );
    }
  }, [canUseBranch, procedure.uid]);

  return (
    <>
      <aside className={styles.root}>
        <div className={styles.inner}>
          {isTabletLargeViewport ? (
            <>
              <h4 className={styles.heading}>Use this Procedure for&nbsp;Free!</h4>
              {canUseBranch && (
                <div className={styles.buttonWrapper}>
                  <Button blurOnClick={true} onClick={showSendModal}>
                    Send it to my phone!
                  </Button>
                </div>
              )}
              <div className={styles.buttonWrapper}>
                <ExternalLink href={desktopUrl}>
                  <Button
                    blurOnClick={true}
                    btnStyle={canUseBranch ? "outline" : "solid"}
                    onClick={handleTagOpenInDesktop}
                  >
                    Open in Desktop App
                  </Button>
                </ExternalLink>
              </div>
            </>
          ) : (
            <div className={styles.buttonWrapper} ref={staticButtonRef}>
              <Button blurOnClick={true} btnStyle="outline" onClick={handleOpenDeepLink}>
                Use for Free in Maintainx App
              </Button>
            </div>
          )}
        </div>
        <button className={styles.downloadLink} type="button" onClick={showDownloadModal}>
          Download as PDF <DownloadIcon width={12} height={12} />
        </button>
        {isShowingFloatingButton ? (
          <div className={styles.floatingButtonWrapper}>
            <Button
              blurOnClick={true}
              btnStyle="outline"
              floating={true}
              onClick={handleOpenDeepLink}
            >
              Use for Free in Maintainx App
            </Button>
          </div>
        ) : null}
      </aside>
      <ModalDownloadPdf
        procedureUid={procedure.uid}
        show={isShowingDownloadModal}
        onDownload={onDownload}
        onHide={hideDownloadModal}
      />
      <ModalSendToPhone procedure={procedure} show={isShowingSendModal} onHide={hideSendModal} />
    </>
  );
}
